import React from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { cn } from "../../lib/utils";
import apiService from "../../services/ApiService";
import type { TreeItem, Tree, ScenarioState, PatchTreeUpdater } from "./types";
import { getBrothersNames, removeElemFromTree } from "./utils/treeUtils";

interface FillNameFormProps {
  item: TreeItem;
  tree: Tree;
  setEditing: (editing: boolean) => void;
  reloadState: () => void;
  currentState: ScenarioState;
  patchTree: (updater: PatchTreeUpdater) => void;
}

export default function FillNameForm({
  item,
  tree,
  setEditing,
  reloadState,
  currentState,
  patchTree,
}: FillNameFormProps) {
  const brothersNames = getBrothersNames(item, tree);
  const variant = item.kind as "Input" | "Output";

  const updateItem = (updates: Partial<TreeItem>) => {
    patchTree((prev) => {
      const sectionKey = variant;
      const section = prev[sectionKey];
      if (!section) return prev;
      const updatedItem = { ...section.tree.items[item.id], ...updates };
      return {
        ...prev,
        [sectionKey]: {
          ...section,
          tree: {
            ...section.tree,
            items: { ...section.tree.items, [item.id]: updatedItem },
          },
        },
      };
    });
  };

  const formik = useFormik({
    initialValues: { name: item.name },
    onSubmit: (values) => {
      if (values.name.length) {
        if (!brothersNames.includes(values.name)) {
          updateItem({ nameColor: null });
          apiService
            .putModelData(item.id, values.name, item.position)
            .then(async (response) => {
              if (response) {
                const newItem = await response.json();
                patchTree((prev) => {
                  const inputItems = { ...prev.Input.tree.items };
                  inputItems[item.id] = { ...inputItems[item.id], name: newItem.name, editing: false };

                  const outputItems = { ...prev.Output.tree.items };
                  if (item.has_related_output && item.relatedModelDataId && outputItems[item.relatedModelDataId]) {
                    outputItems[item.relatedModelDataId] = { ...outputItems[item.relatedModelDataId], name: newItem.name };
                  }

                  return {
                    ...prev,
                    isComputed: false,
                    isSync: false,
                    Input: { ...prev.Input, tree: { ...prev.Input.tree, items: inputItems } },
                    Output: { ...prev.Output, tree: { ...prev.Output.tree, items: outputItems } },
                  };
                });
                setEditing(false);
              }
            });
        } else {
          updateItem({ nameColor: "red" });
        }
      }
    },
  });

  const handleCancel = () => {
    if (item.name === "") {
      removeElemFromTree(item.id, currentState, reloadState);
      setEditing(false);
    } else {
      updateItem({ editing: false });
      setEditing(false);
    }
  };

  return (
    <div>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
          <div className="flex flex-row h-10">
            <input
              type="text"
              {...formik.getFieldProps("name")}
              className={cn(
                "ml-[13.6px] mt-1 w-full border-b border-slate-300 bg-transparent px-0 py-1 text-sm focus:outline-none focus:border-slate-900",
                item.nameColor === "red" ? "text-red-600" : "text-black",
              )}
            />
            <div className="flex flex-row h-[35px]">
              <button
                type="submit"
                className="rounded-full ml-1 mt-1 p-1 hover:bg-slate-100"
                aria-label="confirm"
              >
                <span className="material-symbols-outlined text-[20px]">
                  done
                </span>
              </button>
              <button
                type="button"
                className="rounded-full mt-1 p-1 hover:bg-slate-100"
                aria-label="cancel"
                onClick={(e) => {
                  handleCancel();
                  e.stopPropagation();
                }}
              >
                <span className="material-symbols-outlined text-[20px]">
                  close
                </span>
              </button>
            </div>
          </div>
        </Form>
      </FormikProvider>
    </div>
  );
}
